/**
 * Editorial About Page - Scroll Animations & Parallax Effects
 * Luxury editorial experience with smooth scroll reveals and parallax images
 */

class EditorialAbout {
  constructor() {
    this.parallaxElements = [];
    this.revealElements = [];
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.setupParallax();
    this.setupScrollReveal();
    this.setupPrincipleReveal();
    this.bindEvents();
    
    // Initial check for elements in viewport
    this.checkViewport();
  }

  /**
   * Parallax Effect for Images
   * Smooth vertical movement based on scroll position
   */
  setupParallax() {
    const parallaxSections = document.querySelectorAll('[data-parallax]');
    
    parallaxSections.forEach(section => {
      const wrapper = section.querySelector('.editorial-image-wrapper');
      const image = wrapper?.querySelector('.editorial-image');
      
      if (wrapper && image) {
        // Detect image orientation
        const orientation = this.detectImageOrientation(image);
        wrapper.setAttribute('data-orientation', orientation);
        
        this.parallaxElements.push({
          section,
          wrapper,
          image,
          orientation,
          speed: 0.3 // Noticeable editorial-style parallax
        });
      }
    });
  }
  
  /**
   * Detect Image Orientation
   */
  detectImageOrientation(image) {
    const aspectRatio = image.naturalWidth / image.naturalHeight;
    
    if (aspectRatio > 1.3) {
      return 'landscape'; // 16:9, 4:3, etc.
    } else if (aspectRatio < 0.8) {
      return 'portrait'; // Vertical photos
    } else {
      return 'square'; // Close to 1:1
    }
  }

  /**
   * Scroll Reveal for Sections
   * Fade in and slide up animation when sections enter viewport
   */
  setupScrollReveal() {
    const sections = document.querySelectorAll('.editorial-section');
    
    sections.forEach(section => {
      this.revealElements.push({
        element: section,
        threshold: 0.15,
        revealed: false
      });
    });
  }

  /**
   * Principle Items Reveal
   * Staggered animation for principle blocks
   */
  setupPrincipleReveal() {
    const principles = document.querySelectorAll('.editorial-principle');
    
    principles.forEach((principle, index) => {
      this.revealElements.push({
        element: principle,
        threshold: 0.2,
        revealed: false,
        delay: index * 100 // Stagger delay
      });
    });
  }

  /**
   * Bind Scroll Events
   * Use requestAnimationFrame for smooth performance
   */
  bindEvents() {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.updateParallax();
          this.checkViewport();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Throttled scroll event
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Handle resize
    window.addEventListener('resize', () => {
      this.checkViewport();
    }, { passive: true });
  }

  /**
   * Update Parallax Position
   * Calculate and apply transform based on scroll
   */
  updateParallax() {
    this.parallaxElements.forEach(({ section, wrapper, image, orientation, speed }) => {
      const rect = section.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Only calculate if element is in or near viewport
      if (rect.top < windowHeight && rect.bottom > 0) {
        // Calculate scroll progress (0 to 1)
        const scrollProgress = (windowHeight - rect.top) / (windowHeight + rect.height);
        
        // Different parallax behavior based on orientation
        if (orientation === 'portrait') {
          // For portrait images, use subtle scale instead of translate
          const scale = 1 + (scrollProgress * 0.05); // 1 to 1.05
          image.style.transform = `scale(${scale})`;
          wrapper.style.transform = 'translate3d(0, 0, 0)';
        } else {
          // For landscape/square, use traditional parallax
          const scrolled = windowHeight - rect.top;
          const parallaxOffset = scrolled * speed;
          
          // Clamp to prevent showing background
          const maxOffset = section.offsetHeight * 0.1;
          const clampedOffset = Math.max(-maxOffset, Math.min(maxOffset, parallaxOffset));
          
          wrapper.style.transform = `translate3d(0, ${clampedOffset}px, 0)`;
          image.style.transform = 'scale(1)';
        }
      }
    });
  }

  /**
   * Check Viewport and Reveal Elements
   * Intersection-based reveal with fallback
   */
  checkViewport() {
    const windowHeight = window.innerHeight;

    this.revealElements.forEach(item => {
      if (item.revealed) return;

      const rect = item.element.getBoundingClientRect();
      const elementTop = rect.top;
      const elementBottom = rect.bottom;
      const threshold = windowHeight * (1 - item.threshold);

      // Check if element is in viewport
      if (elementTop < threshold && elementBottom > 0) {
        this.revealElement(item);
      }
    });
  }

  /**
   * Reveal Element with Optional Delay
   */
  revealElement(item) {
    const delay = item.delay || 0;

    setTimeout(() => {
      item.element.classList.add('is-visible');
      item.revealed = true;
    }, delay);
  }

  /**
   * Intersection Observer (Modern Browser Enhancement)
   * More efficient than scroll events for reveal animations
   */
  setupIntersectionObserver() {
    // Check for IntersectionObserver support
    if (!('IntersectionObserver' in window)) {
      return; // Fallback to scroll-based reveal
    }

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: [0, 0.15, 0.5, 1]
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.15) {
          const item = this.revealElements.find(
            item => item.element === entry.target
          );
          
          if (item && !item.revealed) {
            this.revealElement(item);
          }
        }
      });
    }, observerOptions);

    // Observe all reveal elements
    this.revealElements.forEach(item => {
      observer.observe(item.element);
    });
  }
}

// Initialize when script loads
const editorialAbout = new EditorialAbout();

// Smooth scroll for anchor links (if any)
document.querySelectorAll('a[href^="#"]').forEach(anchor => {
  anchor.addEventListener('click', function(e) {
    const href = this.getAttribute('href');
    if (href === '#') return;
    
    const target = document.querySelector(href);
    if (target) {
      e.preventDefault();
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  });
});

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = EditorialAbout;
}
